<template>
  <div class="autoincrement-field" v-if="!field.hidedisplay">
    <!-- Campo derivado (fobtrading): el número es el de la proforma, no se edita ni se genera -->
    <template v-if="isDerivedFromProform">
      <input
        type="text"
        :id="fieldId"
        :name="fieldName"
        :value="fieldValue"
        readonly
        class="form-control readonly-field"
      />
      <small class="form-text text-muted">
        Same as the operation's Proform number — assigned automatically on save.
      </small>
    </template>

    <template v-else>
      <!-- Botón de candado para campos safe -->
      <div v-if="field.safe" class="float-left">
        <button
          v-if="isLock()"
          type="button"
          class="btn btn-sm btn-outline-secondary border-0 buttonn-lock"
          @click="unlockField"
          title="Hacer clic para editar"
        >
          <i class="material-icons">lock</i>
        </button>
        <button
          v-else
          type="button"
          class="btn btn-outline-primary btn-sm border-0 buttonn-lock"
          @click="lockField"
          title="Hacer clic para bloquear"
        >
          <i class="material-icons">lock_open</i>
        </button>
      </div>

      <div class="input-group">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="generateNumber"
          :disabled="(isGenerating || isReadonlyEffective || isLock()) && fieldValue"
          :title="generateButtonTitle"
        >
          <i class="material-icons" v-if="!isGenerating">autorenew</i>
          <span
            v-else
            class="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          {{ generateButtonText }}
        </button>

        <!-- Selector de año -->
        <select
          v-if="field.safe && editingFields === field.key"
          v-model="selectedYear"
          class="form-select"
          style="max-width: 100px"
          @change="onYearChange"
          :disabled="isReadonlyEffective || isLock()"
        >
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
        <input
          type="text"
          :id="fieldId"
          :name="fieldName"
          :value="fieldValue"
          @input="onInputChange"
          @blur="onBlur"
          @focus="onFocus"
          :class="[
            'form-control',
            {
              'is-invalid': hasError,
              'calculated-field': isLock(),
              'readonly-field': isReadonlyEffective,
            },
          ]"
          :required="required"
          :min="min"
          :max="max"
          :step="step"
          :disabled="field.hide"
        />
      </div>

      <!-- Mensaje de error -->
      <div v-if="hasError" class="invalid-feedback d-block">
        {{ errorMessage }}
      </div>

      <!-- Mensaje de éxito -->
      <div v-if="successMessage" class="valid-feedback d-block">
        {{ successMessage }}
      </div>
    </template>
  </div>
</template>

<script>
import { wencesApi, getCurrentClientDbName } from "@/api/wencesApi";
import fieldEditableByMixin from "@/mixins/fieldEditableByMixin";
import { dbNameMatchesTenant } from "@/utils/dbNameMatchesTenant";

export default {
  name: "AutoIncrementField",
  mixins: [fieldEditableByMixin],
  props: {
    field: {
      type: Object,
      required: true,
    },
    value: {
      type: [Number, String],
      default: null,
    },
    modelName: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    editingFields: {
      type: [Object, String],
      default: () => ({}),
    },
    /** Registro que se edita (la ficha lo pasa): su propio número no cuenta como repetido. */
    recordItem: {
      type: Object,
      default: null,
    },
  },
  created() {
    if(this.value) {
      this.newNumber = this.value;
      // El número ya guardado es válido: salir del campo sin tocarlo no lo marca como repetido.
      this.lastValidatedValue = this.value;
    }
    // Proform: el número solo se genera al pulsar Generate (no al abrir el formulario).
    if (this.shouldAutoGenerateOnMount) {
      this.$nextTick(() => {
        this.generateNumber();
      });
    }
  },
  data() {
    return {
      fieldValue: this.value || null,
      hasError: false,
      errorMessage: "",
      successMessage: "",
      isGenerating: false,
      lastValidatedValue: null,
      selectedYear: new Date().getFullYear(), // Default to current year
      // Nuevo: campo para rastrear si el usuario está editando activamente
      activelyEditing: false,
      newNumber: null,
      isPreviewAssignment: false,
      // El backend decide qué campos son "reserve-on-save" y responde preview:true
      isPreviewField: false,
    };
  },
  computed: {
    fieldId() {
      return `field_${this.field.key}`;
    },
    /** Id del registro en edición (ficha: recordItem; formulario: la ruta /edit/:itemId). */
    recordId() {
      const fromItem = this.recordItem && this.recordItem._id;
      const fromRoute = this.$route && this.$route.params && this.$route.params.itemId;
      return fromItem ? String(fromItem) : fromRoute || null;
    },
    fieldName() {
      return this.field.key;
    },
    generateButtonText() {
      return this.isGenerating ? "Generating..." : "Generate";
    },
    generateButtonTitle() {
      return this.isShippingInvoiceNumber || this.isPreviewField
        ? "Show the estimated next number (not reserved until you save)"
        : "Generate number autoincrement automatically";
    },
    availableYears() {
      const currentYear = new Date().getFullYear();
      const years = [];
      for (let i = 0; i < 10; i++) {
        // Show next 10 years
        years.push(currentYear + i);
      }
      return years;
    },
    min() {
      return 1;
    },
    max() {
      return 999999;
    },
    step() {
      return 1;
    },
    isProformModel() {
      return /^proform$/i.test(String(this.modelName || "").trim());
    },
    isShippingInvoiceNumber() {
      return (
        /^shipping$/i.test(String(this.modelName || "").trim()) &&
        this.field &&
        this.field.key === "invoice_number"
      );
    },
    /**
     * fobtrading: shipping.invoice_number = proform_number de la proforma vinculada.
     * El backend lo fija al guardar (hooks/fobtrading/hook_presave), así que acá el campo
     * es solo lectura: sin botón Generate y sin edición manual.
     */
    isDerivedFromProform() {
      return (
        this.isShippingInvoiceNumber &&
        dbNameMatchesTenant(getCurrentClientDbName(), "fobtrading")
      );
    },
    shouldAutoGenerateOnMount() {
      if (this.isProformModel) return false;
      if (this.isShippingInvoiceNumber) return false;
      return !!(this.field.defaultGenerate && !this.fieldValue);
    },
    /** Readonly efectivo: el prop, la definición del campo, o el usuario asignado. */
    isReadonlyEffective() {
      return !!(
        this.readonly ||
        this.field.readonly ||
        !this.canCurrentUserEditThisField
      );
    },
  },
  watch: {
    value(newValue) {
      this.fieldValue = newValue;
      this.newNumber = newValue;
    },
    fieldValue(newValue) {
      this.newNumber = newValue;
      this.$emit("input", newValue);
      this.clearMessages();
    },
  },
  methods: {
    /**
     * Marca el valor como estimación: el padre envía `_invoiceNumberPreview` y el backend
     * reserva el número definitivo al guardar (shipping.invoice_number, nibiru proform_number).
     */
    emitPreviewFlag(isPreview) {
      if (!this.isShippingInvoiceNumber && !this.isPreviewField) return;
      this.isPreviewAssignment = !!isPreview;
      this.$emit("invoice-number-preview", !!isPreview);
    },
    async generateNumber() {
      if (this.isGenerating || this.isReadonlyEffective) return;

      this.isGenerating = true;
      this.clearMessages();

      try {
        const payload = {
          modelName: this.modelName,
          fieldKey: this.field.key,
          customYear: this.selectedYear,
        };
        if (this.isShippingInvoiceNumber) {
          payload.preview = true;
        }

        const response = await wencesApi.post(
          `/api/gotrading/autoincrement/generate`,
          payload
        );

        if (response.data && response.data.number) {
          this.fieldValue = response.data.number;
          if (response.data.preview === true) {
            this.isPreviewField = true;
          }
          if (this.isShippingInvoiceNumber || response.data.preview) {
            this.successMessage =
              response.data.message ||
              `Estimated ${response.data.number} — confirmed on save (not reserved yet)`;
            this.emitPreviewFlag(true);
          } else {
            this.successMessage = response.data.message;
            this.emitPreviewFlag(false);
          }

          // Mostrar información adicional sobre números existentes si está disponible
          if (
            response.data.existingNumbers &&
            response.data.existingNumbers.length > 0 &&
            !this.isShippingInvoiceNumber
          ) {
            this.successMessage += ` (Existing numbers: ${response.data.existingNumbers.join(
              ", "
            )})`;
          }
          this.newNumber = this.fieldValue;
          this.lockField();
          
          // Emitir el valor del campo autoincrement
          this.$emit("input", this.newNumber);
           // Buscar y actualizar el campo de fecha correspondiente
          this.updateDateField();
        } else {
          throw new Error("Could not generate the number");
        }
      } catch (error) {
        console.error("Error generating number:", error);
        this.hasError = true;
        this.errorMessage =
          error.response?.data?.message || "Error generating the number";
      } finally {
        this.isGenerating = false;
      }
    },

    // Método para actualizar automáticamente el campo de fecha (<key>_date)
    // Los campos date se guardan como YYYY-MM-DD: un objeto Date no lo pinta el input ni
    // se persiste igual que el resto de fechas.
    updateDateField() {
      try {
        this.$emit("updateDateField", {
          fieldKey: `${this.field.key}_date`,
          value: new Date().toISOString().split("T")[0],
        });
      } catch (error) {
        console.error("Error updating date field:", error);
      }
    },

    // Método para determinar si el campo está bloqueado (similar a FormInput.vue)
    isLock() {
      // Si el usuario está editando activamente, no bloquear
      if (this.activelyEditing) {
        return false;
      }

      return true;
    },

    async onInputChange(event) {
      const newValue = event.target.value;
      this.fieldValue = newValue === "" ? null : newValue;
      this.newNumber = this.fieldValue;
      this.clearMessages();

      // Marcar que el usuario está editando activamente
      this.activelyEditing = true;
      // Manual edit: no longer a preview assignment
      this.emitPreviewFlag(false);
    },

    async onBlur() {
      // Solo bloquear si el campo es safe y no está en modo edición
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = false;
      }

      if (
        this.fieldValue === null ||
        this.fieldValue === this.lastValidatedValue
      ) {
        return;
      }

      // Validar que el número no esté duplicado
      await this.validateNumber();
    },

    // Nuevo: método para manejar cuando el campo obtiene el foco
    onFocus() {
      // Si el campo es safe y está bloqueado, permitir edición
      if (this.field.safe && this.editingFields !== this.field.key) {
        this.activelyEditing = true;
      }
    },

    async validateNumber() {
      if (!this.fieldValue || this.fieldValue === this.lastValidatedValue) {
        return;
      }

      try {
        const response = await wencesApi.post(
          `/api/gotrading/autoincrement/validate`,
          {
            modelName: this.modelName,
            fieldKey: this.field.key,
            value: this.fieldValue,
            excludeId: this.recordId,
          }
        );

        if (response.data && response.data.exists) {
          this.hasError = true;
          this.errorMessage = response.data.message;

          // Si hay un siguiente número disponible, sugerirlo
          if (response.data.nextAvailableId) {
            this.errorMessage += `. You can use the ${response.data.nextAvailableId} or click on "Generate" to get the next available number.`;
          }
        } else {
          this.lastValidatedValue = this.fieldValue;
          this.successMessage = response.data.message;
        }
      } catch (error) {
        console.error("Error validating number:", error);
        this.hasError = true;
        this.errorMessage = "Error validating the number";
      }
    },

    onYearChange() {
      // Formatos sin año (nibiru: número plano en proforma, NL<n> en invoice_number):
      // el año no forma parte del valor, no hay nada que reescribir.
      const current = this.fieldValue ? this.fieldValue.toString().trim() : "";
      if (current && !/^\d+\/\d+$/.test(current)) {
        return;
      }

      // Extract the number part if it exists, otherwise use 1
      let numberPart = 1;
      if (this.fieldValue) {
        const match = this.fieldValue.toString().match(/^(\d+)\/\d+$/);
        if (match) {
          numberPart = parseInt(match[1]);
        }
      }

      // Update the field value with new year
      this.fieldValue = `${numberPart}/${this.selectedYear}`;
      this.newNumber = this.fieldValue;
      this.$emit("input", this.newNumber);
      this.clearMessages();
    },

    clearMessages() {
      this.hasError = false;
      this.errorMessage = "";
      this.successMessage = "";
    },

    // Nuevo: método para bloquear manualmente el campo
    lockField() {
      this.editingFields = "";
      this.activelyEditing = false;
    },

    // Nuevo: método para desbloquear manualmente el campo
    unlockField() {
      this.editingFields = this.field.key;
      this.activelyEditing = true;
    },
  },
};
</script>

<style scoped>
.autoincrement-field {
  width: 100%;
  position: relative;
}

.input-group {
  display: flex;
  align-items: stretch;
}

.input-group .btn {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0;
  white-space: nowrap;
}

.input-group .form-select {
  border-radius: 0;
  border-left: 0;
  border-right: 0;
  max-width: 100px;
}

.input-group .form-control {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  flex: 1;
}

.buttonn-lock {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.2rem;
}

.calculated-field {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  cursor: not-allowed;
}

.readonly-field {
  background-color: #e9ecef !important;
  color: #495057 !important;
}

.input-group .btn:focus {
  box-shadow: none;
}

.spinner-border {
  width: 1rem;
  height: 1rem;
}

.invalid-feedback,
.valid-feedback {
  display: block;
  margin-top: 0.25rem;
  font-size: 0.875rem;
}

.buttonn-lock {
  padding: 0 !important;
  position: absolute;
  right: 4px;
  top: -1px;
  z-index: 999;
  background-color: white;
  border: 1px solid #dee2e6;
}

.buttonn-lock:hover {
  background-color: #f8f9fa;
}

/* Estilos para campos deshabilitados */
.form-control:disabled {
  background-color: #e9ecef;
  opacity: 0.65;
}

/* Estilos para campos readonly */
.form-control[readonly] {
  background-color: #f0f0f0;
  color: #a0a0a0;
  border: 1px solid #dcdcdc;
}

/* Estilos para campos calculados (bloqueados) */
.calculated-field {
  background-color: #f8f9fa !important;
  color: #6c757d !important;
  border: 1px solid #dee2e6 !important;
}

/* Estilos para campos readonly */
.readonly-field {
  background-color: #f0f0f0 !important;
  color: #a0a0a0 !important;
  border: 1px solid #dcdcdc !important;
}

/* Posicionamiento del botón de candado */
.float-left {
  float: left;
  margin-right: 8px;
}
</style>
